import { UI } from '../../UI.ts'
import { escape_html } from '../../html.ts'
import { AnimationPlayer } from './AnimationPlayer.ts'

import {
  type AnimationClip, AnimationMixer, type SkinnedMesh, type AnimationAction, Object3D, type Scene
} from 'three'

import { AnimationUtility } from './AnimationUtility.ts'
import { AnimationLoader, type AnimationLoadProgress } from './AnimationLoader.ts'
import { CustomAnimationImporter } from './CustomAnimationImporter.ts'
import { type ModelVariationSwitcher } from './ModelVariationSwitcher.ts'

import { SkeletonType } from '../../enums/SkeletonType.ts'
import { Utility } from '../../Utilities.ts'
import { type ThemeManager } from '../../ThemeManager.ts'
import { AnimationSearch } from './AnimationSearch.ts'
import { type AnimationClipMetadata, type TransformedAnimationClipPair } from './interfaces/TransformedAnimationClipPair.ts'
import { UserClipController } from '../../../features/timeline/UserClipController.ts'
import { UserClipStorage } from '../../../features/persistence/UserClipStorage.ts'
import { ProjectArchive } from '../../../features/persistence/ProjectArchive.ts'
import { import_project_file } from '../../../features/persistence/ProjectImporter.ts'
import { KeyframeBuilder } from '../../../features/timeline/KeyframeBuilder.ts'
import { ProceduralClipGenerator, type AnimationSpec } from '../../../features/timeline/ProceduralClipGenerator.ts'
import { PoseSnapshotUtil } from '../../../features/timeline/PoseSnapshot.ts'
import { PoseModeController } from '../../../features/timeline/PoseModeController.ts'
import { PoseBonePicker } from '../../../features/timeline/PoseBonePicker.ts'
import { BoneWeightHighlighter } from '../../../features/timeline/BoneWeightHighlighter.ts'
import { ClipUndoRedoStack } from '../../../features/timeline/ClipUndoRedoStack.ts'
import { PoseOps } from '../../../features/timeline/PoseOps.ts'
import { type PoseSnapshot } from '../../../features/timeline/PoseSnapshot.ts'
import { ClipThumbnailCapture } from '../../../features/timeline/ClipThumbnailCapture.ts'
import { MocapController, type MocapState } from '../../../features/mocap/MocapController.ts'
import { DraggablePanel } from '../../../features/mocap/DraggablePanel.ts'
import { MocapBoneSettings } from '../../../features/mocap/MocapBoneSettings.ts'
import { DEFAULT_HUMANOID_DRIVE_SPEC } from '../../../features/mocap/PoseToRig.ts'
import { OfflineVideoMocapProcessor } from '../../../features/mocap/OfflineVideoMocapProcessor.ts'
import { FootLockProcessor } from '../../../features/mocap/FootLockProcessor.ts'
import { OnionSkinManager } from '../../../features/timeline/OnionSkinManager.ts'
import { ClipBlender } from '../../../features/timeline/ClipBlender.ts'
import { ClipTrimmer } from '../../../features/timeline/ClipTrimmer.ts'
import { CurvesEditor } from '../../../features/timeline/CurvesEditor.ts'
import { ModalDialog } from '../../ModalDialog.ts'
import { confirm_action } from '../../ConfirmationDialog.ts'
import { prompt_fields, prompt_text } from '../../FormDialog.ts'
import { RigConfig } from '../../RigConfig.ts'
import { type TransformControls } from 'three/examples/jsm/controls/TransformControls.js'

// Note: EventTarget is a built-ininterface and do not need to import it
export class StepAnimationsListing extends EventTarget {
  private readonly theme_manager: ThemeManager
  private readonly ui: UI
  private readonly animation_player: AnimationPlayer
  private animation_clips_loaded: TransformedAnimationClipPair[] = []
  private readonly animation_loader: AnimationLoader = new AnimationLoader()

  private animation_mixer: AnimationMixer = new AnimationMixer(new Object3D())
  private skinned_meshes_to_animate: SkinnedMesh[] = []
  private model_variation_switcher: ModelVariationSwitcher | null = null
  private current_playing_index: number = -1
  private skeleton_type: SkeletonType = SkeletonType.Human
  private pose_mode_controller: PoseModeController | null = null
  private pose_bone_picker: PoseBonePicker | null = null
  private readonly bone_weight_highlighter: BoneWeightHighlighter = new BoneWeightHighlighter()
  /** Per user-authored clip pair: an isolated undo/redo history. WeakMap so dropped pairs free their stacks automatically. */
  private readonly undo_redo_stacks: WeakMap<TransformedAnimationClipPair, ClipUndoRedoStack> = new WeakMap()
  /** In-memory pose clipboard. Set by `copy_pose_at_playhead`, consumed by `paste_pose_at_playhead`. */
  private pose_clipboard: PoseSnapshot | null = null
  /** Set by the engine on bootstrap. Used as the source for thumbnail captures of user-authored clips. */
  private renderer_canvas: HTMLCanvasElement | null = null
  /** Debounced thumbnail capture timer. Avoids re-rendering a thumbnail on every bake during a flurry. */
  private thumbnail_debounce_timer: ReturnType<typeof setTimeout> | null = null
  private readonly mocap_controller: MocapController = new MocapController()
  private mocap_preview_draggable: DraggablePanel | null = null
  private current_animation_actions: AnimationAction[] = []
  /** When non-null, mocap pose-update callbacks are baking into the clip at index `recording_target_clip_index`, with timestamps relative to `recording_start_time_seconds`. */
  private recording_start_time_seconds: number | null = null
  private recording_target_clip_index: number = -1
  /** Auto-keyframe mode: when true, releasing the rotate gizmo on a posed bone
   *  immediately bakes the resulting pose into the active user-authored clip
   *  at the current playhead. Lets users block out animation by scrubbing +
   *  posing without clicking Bake every time. Persists to localStorage. */
  private auto_keyframe_enabled: boolean = false
  /** Onion-skin ghost overlay manager. Lazy-instantiated when the user toggles it on. */
  private onion_skin_manager: OnionSkinManager | null = null
  /** Scene reference, set by the engine during bootstrap. Required for onion skinning. */
  private scene: Scene | null = null
  /** Open curves-editor dialog. Tracked so we can force-close it when the
   *  active clip changes (otherwise edits would silently land on the wrong
   *  clip). Null when no dialog is open. */
  private open_curves_editor_dialog: ModalDialog | null = null
  /** Hooks injected by the engine so per-clip export can run without the
   *  step having to know about the export pipeline itself. */
  private export_clip_dispatcher: ((clip: AnimationClip, filename: string) => void) | null = null
  /** Debounce timer for IndexedDB saves of user clips. Flushed on a short trailing delay so a flurry of bakes doesn't hammer IDB. */
  private save_debounce_timer: ReturnType<typeof setTimeout> | null = null

  private animations_file_path: string = 'animations/'

  // retrieved from load skeleton step
  // we will use this to scale all position animation keyframes (uniform scale)
  private skeleton_scale: number = 1.0

  private readonly custom_animation_importer: CustomAnimationImporter

  private _added_event_listeners: boolean = false
  private is_loading_default_animations: boolean = false

  // enable status for mirroring animations
  public mirror_animations_enabled: boolean = false

  // Animation search functionality
  public animation_search: AnimationSearch | null = null

  public set_animations_file_path (path: string): void {
    this.animations_file_path = path
  }

  /**
   * The amount to raise the arms.
   */
  private warp_arm_amount: number = 0.0

  private has_added_event_listeners: boolean = false

  constructor (theme_manager: ThemeManager) {
    super()
    this.ui = UI.getInstance()
    this.animation_player = new AnimationPlayer()
    this.theme_manager = theme_manager

    this.custom_animation_importer = new CustomAnimationImporter(this.animation_loader)

    // fancy way to bind the import context by implementing the function
    // from the CustomAnimationImporter and passing in the current context values. this allows
    // the CustomAnimationImporter to be decoupled from the StepAnimationsListing
    this.custom_animation_importer.set_import_context_provider(() => {
      return {
        skinned_meshes_to_animate: this.skinned_meshes_to_animate,
        skeleton_scale: this.skeleton_scale
      }
    })

    this.custom_animation_importer.addEventListener('import-success', (event: Event) => {
      const new_clips = (event as CustomEvent<TransformedAnimationClipPair[]>).detail
      this.animation_clips_loaded.push(...new_clips)
      this.onAllAnimationsLoaded()
    })
  }

  public begin (skeleton_type: SkeletonType, skeleton_scale: number): void {
    this.skeleton_scale = skeleton_scale

    if (this.ui.dom_current_step_element != null) {
      this.ui.dom_current_step_element.innerHTML = 'Test animations'
    }

    if (this.ui.dom_skinned_mesh_tools != null) {
      this.ui.dom_skinned_mesh_tools.style.display = 'flex'
    }

    if (this.ui.dom_skinned_mesh_animation_tools != null) {
      this.ui.dom_skinned_mesh_animation_tools.style.display = 'flex'
    }

    // bone display toggle only works in animation preview since
    // everything is setup by now
    if (this.ui.dom_show_skeleton_container != null) {
      this.ui.dom_show_skeleton_container.style.display = 'inline-flex'
    }

    this.reset_step_data()
    this.custom_animation_importer.set_enabled(!this.is_loading_default_animations)

    this.skeleton_type = skeleton_type

    // if we are navigating back to this step, we don't want to add the event listeners again
    if (!this._added_event_listeners) {
      this.add_event_listeners()
      this._added_event_listeners = true
    }

    this.update_download_button_enabled()
    this.update_authoring_buttons_enabled_state()
  }

  public reset_step_data (): void {
    // reset previous state if we are re-entering this step
    // this will happen if we are reskinning the mesh after changes

    // Dispose the previous mixer's internal action/clip cache before
    // reassigning. Three.js's AnimationMixer caches AnimationAction objects
    // that hold references to the previous skeleton's bones and clips —
    // without uncacheRoot they survive past the model switch and leak.
    if (this.animation_mixer !== undefined) {
      this.animation_mixer.stopAllAction()
      this.animation_mixer.uncacheRoot(this.animation_mixer.getRoot())
    }

    this.animation_clips_loaded = []
    this.skinned_meshes_to_animate = []
    this.animation_mixer = new AnimationMixer(new Object3D())
    this.current_playing_index = -1
    this.animation_search = null
    this.reset_ui_elements()
    this.animation_player.clear_animation()
  }

  // If we change skeleton types, we need to reset some of the state 
  // in the case we selected animations. This clears the UI state associated with that.
  private reset_ui_elements (): void {
    if (this.ui.dom_animation_clip_list !== null) {
      this.ui.dom_animation_clip_list.innerHTML = ''
    }

    if (this.ui.dom_animation_count !== null) {
      this.ui.dom_animation_count.innerHTML = '0'
    }

    if (this.ui.dom_animations_listing_count !== null) {
      this.ui.dom_animations_listing_count.innerHTML = '0 animations'
    }

    if (this.ui.dom_export_button !== null) {
      this.ui.dom_export_button.disabled = true
    }
  }

  public mixer (): AnimationMixer {
    return this.animation_mixer
  }

  /**
   * Look up the loaded clip pair that owns the given AnimationClip. Used by
   * the per-clip export dispatcher to recover the metadata (e.g., thumbnail
   * data URL) for the clip about to be exported. Compares by reference and
   * falls back to a name match for cases where the dispatcher receives a
   * cloned clip.
   */
  /**
   * Expose the loaded clip pairs to engine-level features (animation layers,
   * mocap recorder, etc.) without giving them a mutable handle to the array.
   */
  public get_loaded_clip_pairs (): readonly TransformedAnimationClipPair[] {
    return this.animation_clips_loaded
  }

  public find_pair_by_clip (clip: AnimationClip): TransformedAnimationClipPair | undefined {
    let by_ref = this.animation_clips_loaded.find(p => p.display_animation_clip === clip)
    if (by_ref !== undefined) return by_ref
    by_ref = this.animation_clips_loaded.find(p => p.display_animation_clip.name === clip.name)
    return by_ref
  }

  // setup in the bootstrap.ts file and only called if we are actively
  // on this step
  public frame_change (delta_time: number): void {
    // Skip the mixer in Pose Mode AND in Mocap Mode so the manual rotations the
    // user is producing (gizmo or webcam) aren't overwritten by the mixer's next
    // sample. In Mocap Mode, MocapController writes bone quaternions in its own
    // RAF loop independent of this frame_change.
    const mixer_should_run =
      this.pose_mode_controller?.is_active() !== true &&
      !this.mocap_controller.is_active()
    if (mixer_should_run) {
      this.mixer().update(delta_time)
    }
    this.animation_player.update(delta_time)

    // Drive onion-skin ghosts to the same playhead each frame so they track
    // the live animation. update() is a no-op when the manager isn't enabled.
    if (this.onion_skin_manager !== null && this.onion_skin_manager.is_enabled()) {
      this.onion_skin_manager.update(this.animation_player.current_time())
    }
  }

  /**
   * Wire the renderer's canvas so this step can capture per-clip thumbnails.
   * Called once during engine bootstrap.
   */
  public set_renderer_canvas (canvas: HTMLCanvasElement): void {
    this.renderer_canvas = canvas
  }

  /** Receive the engine's scene so onion-skin ghosts and other overlays can be added/removed. */
  public set_scene (scene: Scene): void {
    this.scene = scene
  }

  /**
   * Wire the engine's per-clip export pipeline. The dispatcher receives the
   * clip + filename and runs it through StepExportToFile + DownloadSettings.
   * Called once at engine bootstrap; the click handler on each clip card's
   * download button fires `export_single_clip_as_glb` which in turn invokes
   * the dispatcher.
   */
  public set_export_clip_dispatcher (dispatcher: (clip: AnimationClip, filename: string) => void): void {
    this.export_clip_dispatcher = dispatcher
  }

  /**
   * Trigger a single-clip GLB export. Prompts for a filename (default:
   * sanitized clip name) and dispatches into the engine's export pipeline.
   * The current bone-naming-structure choice from DownloadSettings applies.
   */
  public async export_single_clip_as_glb (animation_index: number): Promise<void> {
    const pair = this.animation_clips_loaded[animation_index]
    if (pair === undefined) {
      new ModalDialog('Cannot export', 'No clip selected.').show()
      return
    }
    if (this.export_clip_dispatcher === null) {
      new ModalDialog('Cannot export', 'Export pipeline not initialized.').show()
      return
    }
    const clip = pair.display_animation_clip
    const default_filename = sanitize_filename(clip.name) || `clip_${animation_index}`
    const user_filename = await prompt_text({
      title: 'Export clip',
      label: 'Filename for exported clip (no extension)',
      value: default_filename,
      submit_label: 'Export'
    })
    if (user_filename === null || user_filename.trim() === '') return
    this.export_clip_dispatcher(clip, sanitize_filename(user_filename) || default_filename)
  }

  /**
   * Wires the global TransformControls (owned by Mesh2MotionEngine) so this step can
   * drive Pose Mode. Called once during engine bootstrap.
   *
   * Also installs the bone picker panel + weight highlighter listeners. Both pipe
   * off the controller's selection events so viewport-click and picker-click paths
   * stay synchronized.
   */
  public install_pose_mode_controller (transform_controls: TransformControls): void {
    const controller = new PoseModeController(transform_controls)
    this.pose_mode_controller = controller

    if (this.ui.dom_pose_bone_picker_container !== null) {
      this.pose_bone_picker = new PoseBonePicker({
        container: this.ui.dom_pose_bone_picker_container,
        on_bone_picked: (bone) => { controller.select_bone(bone) }
      })
    }

    // One subscription handles both selection effects: highlight the rigged
    // vertices AND mirror the selection in the picker panel.
    controller.add_selection_listener((bone) => {
      this.bone_weight_highlighter.highlight_bone(bone)
      this.pose_bone_picker?.set_selected_bone(bone)
    })
  }

  public is_pose_mode_active (): boolean {
    return this.pose_mode_controller?.is_active() ?? false
  }

  /**
   * Skeleton the engine should raycast against on a pose-mode mousedown. Returns
   * null when pose mode is off — caller falls back to the normal skeleton source.
   */
  public get_pose_mode_raycast_skeleton (): import('three').Skeleton | null {
    return this.pose_mode_controller?.get_skeleton_for_raycast() ?? null
  }

  /**
   * Forward a viewport-click bone selection to the controller so all selection
   * listeners (highlighter, picker UI) fire from a single place. The engine calls
   * this from `handle_pose_mode_mouse_down` after raycasting.
   */
  public select_pose_mode_bone (bone: import('three').Bone): void {
    this.pose_mode_controller?.select_bone(bone)
  }

  public toggle_pose_mode (): void {
    if (this.pose_mode_controller === null) return
    if (this.skinned_meshes_to_animate.length === 0) return

    // Pose Mode and Mocap conflict — both write bone rotations. Stop mocap first
    // when entering Pose Mode.
    if (!this.pose_mode_controller.is_active() && this.mocap_controller.is_active()) {
      this.exit_mocap()
    }

    this.pose_mode_controller.set_active_targets(
      this.skinned_meshes_to_animate[0],
      this.current_animation_actions
    )
    const will_be_active = this.pose_mode_controller.toggle()
    this.update_pose_mode_button_state()

    if (will_be_active) {
      this.activate_pose_mode_helpers()
    } else {
      this.deactivate_pose_mode_helpers()
    }
  }

  /** Force-exit pose mode. Used when navigating away from this step. */
  public exit_pose_mode (): void {
    if (this.pose_mode_controller?.is_active() === true) {
      this.deactivate_pose_mode_helpers()
    }
    this.pose_mode_controller?.exit()
    this.update_pose_mode_button_state()
  }

  /** Spin up the bone picker + weight highlighter for the active mesh. */
  private activate_pose_mode_helpers (): void {
    const active_mesh = this.skinned_meshes_to_animate[0] ?? null
    this.pose_bone_picker?.set_skeleton(active_mesh)
    this.pose_bone_picker?.set_selected_bone(this.pose_mode_controller?.get_selected_bone() ?? null)
    this.pose_bone_picker?.show()

    if (this.skinned_meshes_to_animate.length > 0) {
      this.bone_weight_highlighter.apply(this.skinned_meshes_to_animate)
    }
  }

  /** Tear down the bone picker + weight highlighter when leaving pose mode. */
  private deactivate_pose_mode_helpers (): void {
    this.pose_bone_picker?.hide()
    this.bone_weight_highlighter.dispose()
  }

  /**
   * Toggles webcam mocap. Mutually exclusive with Pose Mode — entering one exits
   * the other. First activation lazily downloads ~10MB of MediaPipe models from
   * CDN, so the button shows a loading state during `start()`.
   */
  public async toggle_mocap (): Promise<void> {
    if (this.skinned_meshes_to_animate.length === 0) return

    if (this.mocap_controller.is_active()) {
      await this.mocap_controller.stop()
      this.update_mocap_button_state()
      this.update_mocap_preview_visibility()
      return
    }

    // Mocap and Pose Mode conflict — both want to write bone rotations.
    this.exit_pose_mode()
    // Onion-skin ghosts can't track real-time mocap input meaningfully —
    // they'd render the previously-recorded clip while the live skeleton
    // moves to a different pose. Disable to avoid the visual mismatch.
    this.disable_onion_skin()

    const preview_container = this.ui.dom_mocap_preview_container ?? undefined
    const rig = RigConfig.by_skeleton_type(this.skeleton_type)
    await this.mocap_controller.start({
      skinned_mesh: this.skinned_meshes_to_animate[0],
      preview_container,
      disabled_bones: MocapBoneSettings.load_disabled_bones(),
      root_motion_bone_name: rig?.position_tracking_bone_name,
      root_motion_enabled: MocapBoneSettings.load_root_motion_enabled(),
      smoothing_strength: MocapBoneSettings.load_smoothing_strength(),
      hands_enabled: MocapBoneSettings.load_hands_enabled(),
      on_state_change: (state: MocapState, error?: Error) => {
        this.update_mocap_button_state()
        this.update_mocap_preview_visibility(state)
        // If mocap drops out (e.g. webcam disconnected) while recording, stop recording too.
        if ((state === 'idle' || state === 'error') && this.is_recording()) {
          this.stop_recording()
        }
        if (state === 'error' && error !== undefined) {
          new ModalDialog('Mocap failed', error.message).show()
        }
      },
      on_pose_update: (frame_time: number) => {
        this.handle_mocap_pose_update(frame_time)
      }
    })
  }

  /** Force-stop mocap. Used when navigating away from this step. */
  public exit_mocap (): void {
    void this.mocap_controller.stop()
    this.update_mocap_button_state()
    this.update_mocap_preview_visibility()
  }

  /**
   * Open the Mocap settings dialog where the user toggles which bones are
   * driven by the webcam. Toggles persist to localStorage immediately AND
   * apply to the live PoseToRig if mocap is currently running.
   *
   * The drivable-bone list is read from the static default spec — that means
   * the dialog works whether or not mocap is currently active. When mocap
   * starts later, `disabled_bones` is read from localStorage and applied.
   */
  public show_mocap_settings (): void {
    const drivable = DEFAULT_HUMANOID_DRIVE_SPEC.map(s => s.bone_name)
    MocapBoneSettings.show({
      drivable_bones: drivable,
      disabled_bones: MocapBoneSettings.load_disabled_bones(),
      root_motion_enabled: MocapBoneSettings.load_root_motion_enabled(),
      smoothing_strength: MocapBoneSettings.load_smoothing_strength(),
      hands_enabled: MocapBoneSettings.load_hands_enabled(),
      on_toggle: (bone_name: string, enabled: boolean) => {
        // Update persisted set
        const current = new Set(MocapBoneSettings.load_disabled_bones())
        if (enabled) current.delete(bone_name)
        else current.add(bone_name)
        MocapBoneSettings.save_disabled_bones(Array.from(current))
        // Apply to live PoseToRig if mocap is running (no-op otherwise)
        this.mocap_controller.set_bone_enabled(bone_name, enabled)
      },
      on_root_motion_toggle: (enabled: boolean) => {
        MocapBoneSettings.save_root_motion_enabled(enabled)
        this.mocap_controller.set_root_motion_enabled(enabled)
      },
      on_smoothing_strength_change: (strength: number) => {
        MocapBoneSettings.save_smoothing_strength(strength)
        this.mocap_controller.set_smoothing_strength(strength)
      },
      on_hands_toggle: (enabled: boolean) => {
        MocapBoneSettings.save_hands_enabled(enabled)
        // Apply to live MocapController if running. The provider init is async
        // (CDN model download); fire and forget — failures show via console.
        if (enabled) void this.mocap_controller.enable_hands_runtime()
        else void this.mocap_controller.disable_hands_runtime()
      },
      on_process_video_file: (file: File) => {
        // Close the settings dialog before kicking off (the progress modal
        // takes over).
        document.querySelector('.modal-dialog-overlay.mocap-bone-dialog')?.remove()
        void this.process_video_file_for_mocap(file)
      },
      on_apply_foot_lock: () => {
        document.querySelector('.modal-dialog-overlay.mocap-bone-dialog')?.remove()
        this.apply_foot_lock_to_active_clip()
      }
    })
  }

  /**
   * Apply foot-locking IK to the active clip. Detects plant phases per foot
   * and rotates the leg chain (calf + thigh by default) to keep the planted
   * foot's world position constant during each phase. Mutates the clip in
   * place; the user can undo via the per-clip undo stack.
   */
  public apply_foot_lock_to_active_clip (): void {
    if (this.skinned_meshes_to_animate.length === 0) {
      new ModalDialog('Cannot apply foot lock', 'No model loaded.').show()
      return
    }
    if (!this.is_animation_user_authored(this.current_playing_index)) {
      new ModalDialog('Cannot apply foot lock', 'Foot lock only works on user-authored clips. Bake or record one first.').show()
      return
    }
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined) return

    this.push_undo_state_for_active_clip()

    const skeleton = this.skinned_meshes_to_animate[0].skeleton
    const result = FootLockProcessor.apply(pair.display_animation_clip, skeleton)
    pair.display_animation_clip = result.clip
    pair.original_animation_clip = FootLockProcessor.apply(pair.original_animation_clip, skeleton).clip

    // Rebind the mixer to the updated clip and refresh the UI.
    this.play_animation(this.current_playing_index)
    this.schedule_save_user_clips()

    const phases_summary = Object.entries(result.plant_phases_per_foot)
      .map(([foot, count]) => `${foot}: ${count}`)
      .join(', ')
    if (result.frames_adjusted === 0) {
      new ModalDialog('Foot lock', 'No plant phases detected. Try lowering the ground threshold or check that your skeleton has bones named foot_l / foot_r.').show()
    } else {
      new ModalDialog('Foot lock applied', `Adjusted ${result.frames_adjusted} frame(s). Plant phases — ${phases_summary}.`).show()
    }
  }

  /**
   * Process a pre-recorded video file with MediaPipe and bake the result into
   * a new user-authored clip. Shows a progress modal during processing and
   * leaves the new clip selected when done.
   */
  public async process_video_file_for_mocap (file: File): Promise<void> {
    if (this.skinned_meshes_to_animate.length === 0) {
      new ModalDialog('Cannot process video', 'Load a model first — mocap needs a skeleton to drive.').show()
      return
    }
    if (this.mocap_controller.is_active()) {
      new ModalDialog('Cannot process video', 'Stop the live webcam mocap before processing a video file.').show()
      return
    }
    this.exit_pose_mode()
    // Onion ghosts would render the previously-active clip while the
    // skeleton is being driven by the offline processor. Disable so the
    // user only sees the live capture pose.
    this.disable_onion_skin()

    // Create a destination clip for the recording. Same flow as "+ New Clip"
    // followed by a recording — we just don't go through MocapController.
    const new_index = this.create_and_select_user_authored_clip()
    if (new_index === -1) return

    // Set recording state with start_time = 0 so handle_mocap_pose_update
    // treats the processor's per-frame `target_time` as the elapsed offset.
    this.recording_target_clip_index = new_index
    this.recording_start_time_seconds = 0
    this.update_record_button_state()

    // Progress dialog. Replace its body live as frames complete.
    const progress_dialog = new ModalDialog(
      'Processing video',
      `<p id="mocap-video-progress-text">Loading video…</p>
       <progress id="mocap-video-progress-bar" value="0" max="100" style="width: 100%;"></progress>
       <p style="margin-top: 0.75rem;"><button type="button" id="mocap-video-cancel-btn" class="secondary-button">Cancel</button></p>`,
      { customClass: 'mocap-video-progress-dialog' }
    )
    progress_dialog.show()

    const abort_controller = new AbortController()
    const cancel_btn = document.querySelector<HTMLButtonElement>('#mocap-video-cancel-btn')
    cancel_btn?.addEventListener('click', () => abort_controller.abort())

    const rig = RigConfig.by_skeleton_type(this.skeleton_type)
    try {
      const result = await OfflineVideoMocapProcessor.process({
        file,
        skinned_mesh: this.skinned_meshes_to_animate[0],
        root_motion_bone_name: rig?.position_tracking_bone_name,
        root_motion_enabled: MocapBoneSettings.load_root_motion_enabled(),
        disabled_bones: MocapBoneSettings.load_disabled_bones(),
        smoothing_strength: MocapBoneSettings.load_smoothing_strength(),
        abort_signal: abort_controller.signal,
        on_pose_update: (frame_time) => {
          this.handle_mocap_pose_update(frame_time)
        },
        on_progress: ({ frame, total_frames, percent }) => {
          const text = document.querySelector('#mocap-video-progress-text')
          const bar = document.querySelector('#mocap-video-progress-bar') as HTMLProgressElement | null
          if (text !== null) text.textContent = `Frame ${frame} of ${total_frames}`
          if (bar !== null) bar.value = percent
        }
      })
      progress_dialog.remove()
      if (result.aborted) {
        // User cancelled — keep partially-baked clip but warn.
        new ModalDialog('Processing cancelled', `Stopped after ${result.frames_stepped} frame(s). The partial clip is selected.`).show()
      } else if (result.frames_with_pose === 0) {
        new ModalDialog('No pose detected', 'MediaPipe couldn\'t find a person in this video. Check the framing and try again.').show()
      }
    } catch (err) {
      progress_dialog.remove()
      const message = err instanceof Error ? err.message : String(err)
      new ModalDialog('Video processing failed', message).show()
    } finally {
      // Always clear the recording flags + finalize the clip.
      this.stop_recording()
    }
  }

  private update_mocap_button_state (): void {
    const button = this.ui.dom_mocap_button
    if (button === null) return
    const state = this.mocap_controller.get_state()
    const is_active = this.mocap_controller.is_active()
    button.classList.toggle('toggle-active', is_active)
    button.setAttribute('aria-pressed', is_active ? 'true' : 'false')
    button.disabled = state === 'starting' || this.skinned_meshes_to_animate.length === 0
  }

  /**
   * Show/hide the webcam preview overlay and reflect the controller's state via a
   * data attribute (CSS hides the "Loading mocap…" overlay when state is `running`).
   * Optional `state` argument for callers who already know the new state; defaults
   * to whatever the controller currently reports.
   */
  private update_mocap_preview_visibility (state?: MocapState): void {
    const container = this.ui.dom_mocap_preview_container
    if (container === null) return
    const effective_state = state ?? this.mocap_controller.get_state()
    const should_show = effective_state === 'starting' || effective_state === 'running'
    container.hidden = !should_show
    container.dataset.mocapState = effective_state
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Persistence (Phase 6): save/restore user-authored clips via IndexedDB.

  /**
   * Pull saved user-authored clips for this skeleton type out of IndexedDB and
   * append them. Called once per skeleton load. Async so the UI doesn't block.
   */
  public async restore_user_clips_async (): Promise<void> {
    const saved = await UserClipStorage.load(this.skeleton_type)
    if (saved.length === 0) return
    saved.sort((a, b) => a.display_animation_clip.name.localeCompare(b.display_animation_clip.name))
    this.animation_clips_loaded.push(...saved)
    // Re-render the list so the restored clips show up.
    if (this.animation_search !== null) {
      this.animation_search.add_animations(saved)
    }
  }

  /**
   * Sort comparator that keeps user-authored clips visually grouped at the bottom
   * of the list. Library clips and user clips are each sorted alphabetically within
   * their group.
   *
   * Why grouped, not strict alphabetical: the per-clip action bar (rename/duplicate
   * /delete) overflows the fixed 160px item box. With user clips at the bottom the
   * overflow is harmless; mixed in alphabetically, the action bar gets occluded by
   * the next row of items and the buttons effectively vanish.
   */
  private sort_clips_grouped (clips: TransformedAnimationClipPair[]): void {
    clips.sort((a, b) => {
      const a_user = a.metadata.source_type === 'user-authored' ? 1 : 0
      const b_user = b.metadata.source_type === 'user-authored' ? 1 : 0
      if (a_user !== b_user) return a_user - b_user
      return a.display_animation_clip.name.localeCompare(b.display_animation_clip.name)
    })
  }

  /**
   * Trailing-edge debounced save. A burst of bake events (e.g. a 2-second mocap
   * recording at 30fps) collapses to a single IDB write 250ms after the last
   * change. Idempotent — calling it before the timer fires just resets the timer.
   */
  private schedule_save_user_clips (): void {
    if (this.save_debounce_timer !== null) {
      clearTimeout(this.save_debounce_timer)
    }
    this.save_debounce_timer = setTimeout(() => {
      this.save_debounce_timer = null
      void UserClipStorage.save(this.skeleton_type, this.animation_clips_loaded)
    }, 250)
  }

  /**
   * Handle a project-import file: parse it, ask for confirmation, apply to IDB +
   * localStorage, then reload the current skeleton's clips into the UI. Shows a
   * modal on success or error so the user knows what happened.
   *
   * Accepts both v1 `.meshinator.json` and v2 `.meshinator.zip`. v2 archives
   * also bundle the rigged mesh GLB; we trigger a separate browser download
   * for that so the user can load it via the normal model-load flow.
   */
  /** ProjectImportHost: drop in-memory user clips (storage is overwritten separately by ProjectArchive.apply). */
  public drop_user_clips_in_memory (): void {
    this.animation_clips_loaded = this.animation_clips_loaded.filter(
      p => p.metadata.source_type !== 'user-authored'
    )
  }

  /** ProjectImportHost: notify UI that clips changed. */
  public on_clips_changed (): void {
    this.onAllAnimationsLoaded()
  }

  /** Wipe ALL persisted user clips (across every skeleton type) and reload. */
  public async clear_all_saved_user_clips (): Promise<void> {
    await UserClipStorage.clear_all()
    this.drop_user_clips_in_memory()
    this.on_clips_changed()
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Mocap recording (Phase 5.B): bake live pose snapshots into a user clip.

  public is_recording (): boolean {
    return this.recording_start_time_seconds !== null
  }

  /**
   * Toggle recording. Pre-conditions: mocap must be running AND the current clip
   * must be user-authored. Starting a recording RESETS the active clip to empty
   * — re-recording overwrites cleanly rather than leaving stale keyframes.
   */
  public toggle_record_mocap (mode: 'replace' | 'append' = 'replace'): void {
    if (this.is_recording()) {
      this.stop_recording()
      return
    }
    if (this.mocap_controller.get_state() !== 'running') return
    if (!this.is_animation_user_authored(this.current_playing_index)) return
    if (this.skinned_meshes_to_animate.length === 0) return

    // Snapshot the pre-recording clip so undo can roll back the entire take.
    // (Live frames during recording bake without further snapshots — that would
    // produce 30 history entries per second; one per record-take is correct.)
    this.push_undo_state_for_active_clip()

    const active_pair = this.animation_clips_loaded[this.current_playing_index]

    if (mode === 'replace') {
      // Reset the active clip's keyframes so the recording starts on a clean canvas.
      const fresh = KeyframeBuilder.create_empty_clip(active_pair.display_animation_clip.name)
      fresh.duration = 2.0 // nominal canvas; will grow as we bake
      active_pair.display_animation_clip = fresh
      active_pair.original_animation_clip = KeyframeBuilder.create_empty_clip(active_pair.display_animation_clip.name)
      active_pair.original_animation_clip.duration = 2.0

      this.recording_target_clip_index = this.current_playing_index
      this.recording_start_time_seconds = performance.now() / 1000
    } else {
      // Append: keep the existing clip, but offset the recording start time so
      // bake timestamps land at `playhead + delta` rather than `0 + delta`.
      const playhead = this.animation_player.current_time()
      this.recording_target_clip_index = this.current_playing_index
      this.recording_start_time_seconds = (performance.now() / 1000) - playhead
    }

    this.update_record_button_state()
  }

  private stop_recording (): void {
    this.recording_start_time_seconds = null
    this.recording_target_clip_index = -1
    this.update_record_button_state()
    // Re-bind the mixer to the freshly-recorded clip so the user can hit play
    // and immediately watch their take. Doing this after stop (not during) keeps
    // mocap's bone-write loop from fighting the mixer mid-recording.
    if (!this.mocap_controller.is_active()) {
      this.play_animation(this.current_playing_index)
    }
    this.schedule_thumbnail_capture()
  }

  /**
   * Called from MocapController on each successful pose update. If we're recording,
   * snapshot the current bone state and upsert into the active clip at the
   * recording-relative timestamp.
   */
  private handle_mocap_pose_update (frame_time_seconds: number): void {
    if (this.recording_start_time_seconds === null) return
    if (this.recording_target_clip_index === -1) return
    if (this.skinned_meshes_to_animate.length === 0) return

    const elapsed = frame_time_seconds - this.recording_start_time_seconds
    if (elapsed < 0) return

    const active_pair = this.animation_clips_loaded[this.recording_target_clip_index]
    if (active_pair === undefined) {
      // The user must have changed clips (or they got reordered) — abort cleanly.
      this.stop_recording()
      return
    }

    const rig = RigConfig.by_skeleton_type(this.skeleton_type)
    const position_bone_names = rig?.position_tracking_bone_name !== undefined
      ? [rig.position_tracking_bone_name]
      : []
    const snapshot = PoseSnapshotUtil.capture(
      this.skinned_meshes_to_animate[0].skeleton,
      elapsed,
      { position_bone_names }
    )

    active_pair.display_animation_clip = KeyframeBuilder.upsert_pose_keyframe(
      active_pair.display_animation_clip,
      snapshot
    )
    active_pair.original_animation_clip = KeyframeBuilder.upsert_pose_keyframe(
      active_pair.original_animation_clip,
      snapshot
    )
    // Debounced save — a 30Hz bake stream collapses to a single IDB write.
    this.schedule_save_user_clips()
  }

  private update_record_button_state (): void {
    const button = this.ui.dom_record_mocap_button
    const append_button = this.ui.dom_append_record_mocap_button
    const recording = this.is_recording()
    const can_start = this.mocap_controller.get_state() === 'running' &&
      this.is_animation_user_authored(this.current_playing_index) &&
      this.skinned_meshes_to_animate.length > 0

    if (button !== null) {
      button.disabled = !recording && !can_start
      button.classList.toggle('recording-active', recording)
      button.setAttribute('aria-pressed', recording ? 'true' : 'false')
    }
    if (append_button !== null) {
      // Append disabled while a recording is in progress (you can't switch modes
      // mid-take) and disabled when there's nothing to append onto.
      append_button.disabled = recording || !can_start
      append_button.classList.toggle('recording-active', recording)
      append_button.setAttribute('aria-pressed', recording ? 'true' : 'false')
    }
  }

  private update_pose_mode_button_state (): void {
    const button = this.ui.dom_pose_mode_button
    if (button === null) return
    const is_active = this.is_pose_mode_active()
    button.classList.toggle('toggle-active', is_active)
    button.setAttribute('aria-pressed', is_active ? 'true' : 'false')
  }

  /**
   * Returns a list of all of the currently-displayed animation clips.
   */
  public animation_clips (): AnimationClip[] {
    return this.animation_clips_loaded.map(clip => clip.display_animation_clip)
  }

  public get_animation_metadata (index: number): AnimationClipMetadata | null {
    if (index < 0 || index >= this.animation_clips_loaded.length) {
      return null
    }

    return this.animation_clips_loaded[index].metadata
  }

  public is_animation_custom (index: number): boolean {
    return this.get_animation_metadata(index)?.source_type === 'custom-import'
  }

  public is_animation_user_authored (index: number): boolean {
    return this.get_animation_metadata(index)?.source_type === 'user-authored'
  }

  /**
   * Creates a new empty user-authored clip, appends it to the loaded list, rebuilds
   * the UI, and selects (plays) the new clip. The new clip is initialized with the
   * current bind pose at t=0 and a 2-second canvas so it's immediately playable.
   * Returns the new clip's index.
   */
  public create_and_select_user_authored_clip (): number {
    if (this.skinned_meshes_to_animate.length === 0) {
      new ModalDialog('Cannot create clip', user_clip_unavailable_message()).show()
      return -1
    }

    const name = UserClipController.next_unique_clip_name(this.animation_clips_loaded)
    const rig = RigConfig.by_skeleton_type(this.skeleton_type)
    const position_bone_names = rig?.position_tracking_bone_name !== undefined
      ? [rig.position_tracking_bone_name]
      : []

    const new_pair = UserClipController.create_empty_user_clip(
      name,
      this.skinned_meshes_to_animate[0],
      position_bone_names
    )
    this.animation_clips_loaded.push(new_pair)
    this.onAllAnimationsLoaded()

    const new_index = this.animation_clips_loaded.findIndex(p => p === new_pair)
    if (new_index !== -1) {
      this.play_animation(new_index)
    }
    this.update_bake_button_enabled()
    this.schedule_save_user_clips()
    // Capture an initial thumbnail (the bind-pose viewport) so the clip card
    // isn't blank until the user does a bake.
    this.schedule_thumbnail_capture()
    return new_index
  }

  /**
   * Build a user-authored clip from a procedural animation spec (JSON text) and
   * add it to the list. The clip is generated against the live rig: the skeleton
   * is reset to its bind pose first so the spec's per-bone deltas compose onto a
   * known base. Bones the current rig lacks are skipped (surfaced as warnings).
   */
  public import_animation_spec (spec_json: string): void {
    if (this.skinned_meshes_to_animate.length === 0) {
      new ModalDialog('Cannot generate clip', user_clip_unavailable_message()).show()
      return
    }

    let spec: AnimationSpec
    try {
      spec = JSON.parse(spec_json) as AnimationSpec
    } catch (err) {
      new ModalDialog('Invalid spec', `The animation spec is not valid JSON.<br><br>${escape_html(String(err))}`).show()
      return
    }

    if (typeof spec?.name !== 'string' || !Array.isArray(spec?.tracks) || typeof spec?.duration !== 'number') {
      new ModalDialog('Invalid spec', 'The spec must be an object with a string <code>name</code>, a numeric <code>duration</code>, and a <code>tracks</code> array.').show()
      return
    }

    const skeleton = this.skinned_meshes_to_animate[0].skeleton
    // Reset to bind so per-bone deltas compose onto a known base (see generator docs).
    skeleton.pose()

    const { clip, warnings } = ProceduralClipGenerator.generate(spec, skeleton)

    const pair: TransformedAnimationClipPair = {
      original_animation_clip: clip,
      display_animation_clip: clip.clone(),
      metadata: { source_type: 'user-authored', tags: [] }
    }
    this.animation_clips_loaded.push(pair)
    this.onAllAnimationsLoaded()

    const new_index = this.animation_clips_loaded.findIndex(p => p === pair)
    if (new_index !== -1) {
      this.play_animation(new_index)
    }
    this.update_bake_button_enabled()
    this.schedule_save_user_clips()
    this.schedule_thumbnail_capture()

    if (clip.tracks.length === 0) {
      new ModalDialog('No tracks generated', `"${escape_html(spec.name)}" produced no animation tracks. ${warnings.map(escape_html).join('<br>')}`).show()
    } else if (warnings.length > 0) {
      console.warn(`Generated "${spec.name}" with warnings:`, warnings)
    }
  }

  private async rename_user_authored_clip (index: number): Promise<void> {
    const pair = this.animation_clips_loaded[index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return

    const next_name = await prompt_text({
      title: 'Rename clip',
      label: 'Clip name',
      value: pair.display_animation_clip.name,
      submit_label: 'Rename'
    })
    if (next_name === null) return
    if (!UserClipController.rename_user_clip(pair, next_name)) return

    this.refresh_animation_clip_ui(pair)
    this.schedule_save_user_clips()
  }

  private duplicate_user_authored_clip (index: number): void {
    const pair = this.animation_clips_loaded[index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return

    const new_name = UserClipController.next_unique_duplicate_name(
      pair.display_animation_clip.name,
      this.animation_clips_loaded
    )
    const duplicate = UserClipController.duplicate_user_clip(pair, new_name)
    if (duplicate === null) return

    this.animation_clips_loaded.push(duplicate)
    this.refresh_animation_clip_ui(duplicate)
    this.schedule_save_user_clips()
  }

  /**
   * Trim a user-authored clip to a sub-range (in seconds). Replaces the
   * display + original clips in place via ClipTrimmer, which interpolates
   * boundary samples so the trimmed clip starts and ends in the right pose.
   * Persists via the existing user-clip save pipeline.
   */
  private async trim_user_authored_clip (index: number): Promise<void> {
    const pair = this.animation_clips_loaded[index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return
    const duration = pair.display_animation_clip.duration
    const values = await prompt_fields({
      title: `Trim "${pair.display_animation_clip.name}"`,
      submit_label: 'Trim',
      fields: [
        { id: 'start', label: 'Start time (seconds)', type: 'number', value: '0', step: '0.001', min: '0' },
        { id: 'end', label: 'End time (seconds)', type: 'number', value: duration.toFixed(2), step: '0.001', min: '0' }
      ]
    })
    if (values === null) return
    const start_str = values.start
    const end_str = values.end
    const start = parseFloat(start_str)
    const end = parseFloat(end_str)
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || start < 0) {
      new ModalDialog('Trim clip', 'Provide finite times with end > start ≥ 0.').show()
      return
    }
    if (end > duration) {
      new ModalDialog('Trim clip', `End time (${end.toFixed(2)}s) exceeds clip duration (${duration.toFixed(2)}s).`).show()
      return
    }
    try {
      pair.display_animation_clip = ClipTrimmer.trim(pair.display_animation_clip, start, end)
      pair.original_animation_clip = ClipTrimmer.trim(pair.original_animation_clip, start, end)
      this.refresh_animation_clip_ui(pair)
      this.schedule_save_user_clips()
    } catch (err) {
      new ModalDialog('Trim clip', (err as Error).message).show()
    }
  }

  private async delete_user_authored_clip (index: number): Promise<void> {
    const pair = this.animation_clips_loaded[index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return

    const confirmed = await confirm_action({
      title: 'Delete clip',
      message: `Delete "${pair.display_animation_clip.name}"?`,
      confirm_label: 'Delete',
      danger: true
    })
    if (!confirmed) return

    this.animation_clips_loaded.splice(index, 1)
    const next_index = Math.min(index, this.animation_clips_loaded.length - 1)
    const next_pair = next_index >= 0 ? this.animation_clips_loaded[next_index] : undefined
    this.refresh_animation_clip_ui(next_pair)
    this.schedule_save_user_clips()
  }

  private refresh_animation_clip_ui (pair_to_select?: TransformedAnimationClipPair): void {
    this.sort_clips_grouped(this.animation_clips_loaded)

    this.build_animation_clip_ui(this.animation_clips_loaded, this.theme_manager)
    this.bind_animation_search_events()

    const selected_index = pair_to_select !== undefined
      ? this.animation_clips_loaded.findIndex(p => p === pair_to_select)
      : Math.min(this.current_playing_index, this.animation_clips_loaded.length - 1)

    if (selected_index >= 0) {
      this.play_animation(selected_index)
    }
    this.update_bake_button_enabled()
    this.update_record_button_state()
  }

  /**
   * Captures the current bone pose at the scrubber's playhead time and bakes it into
   * the currently-selected clip — but only if that clip is user-authored. Returns
   * true on success.
   */
  public bake_pose_into_active_clip (): boolean {
    const active = this.animation_clips_loaded[this.current_playing_index]
    if (active === undefined) return false
    if (this.skinned_meshes_to_animate.length === 0) return false

    const rig = RigConfig.by_skeleton_type(this.skeleton_type)
    const position_bone_names = rig?.position_tracking_bone_name !== undefined
      ? [rig.position_tracking_bone_name]
      : []

    // Snapshot the pre-bake clip so the user can undo this single bake. Done
    // BEFORE the mutating bake_pose_at_time call so the snapshot captures the
    // pre-bake state.
    this.push_undo_state_for_active_clip()

    const time = this.animation_player.current_time()
    const baked = UserClipController.bake_pose_at_time(
      active,
      this.skinned_meshes_to_animate[0],
      time,
      position_bone_names
    )
    if (!baked) return false

    // Re-bind the new clip to the mixer so playback reflects the freshly baked keyframe.
    this.play_animation(this.current_playing_index)
    this.schedule_save_user_clips()
    this.schedule_thumbnail_capture()
    return true
  }

  private update_bake_button_enabled (): void {
    if (this.ui.dom_bake_pose_button === null) return
    const is_user_clip = this.is_animation_user_authored(this.current_playing_index)
    const has_mesh = this.skinned_meshes_to_animate.length > 0
    this.ui.dom_bake_pose_button.disabled = !(is_user_clip && has_mesh)
  }

  // --- Auto-keyframe mode ------------------------------------------------

  public is_auto_keyframe_enabled (): boolean {
    return this.auto_keyframe_enabled
  }

  public set_auto_keyframe_enabled (enabled: boolean): void {
    this.auto_keyframe_enabled = enabled
    try {
      localStorage.setItem('meshinator-auto-keyframe', enabled ? '1' : '0')
    } catch (_) { /* non-fatal */ }
    this.update_auto_keyframe_button_state()
  }

  public toggle_auto_keyframe (): void {
    this.set_auto_keyframe_enabled(!this.auto_keyframe_enabled)
  }

  public load_auto_keyframe_setting (): void {
    try {
      this.auto_keyframe_enabled = localStorage.getItem('meshinator-auto-keyframe') === '1'
    } catch (_) { this.auto_keyframe_enabled = false }
    this.update_auto_keyframe_button_state()
  }

  private update_auto_keyframe_button_state (): void {
    const btn = document.querySelector<HTMLButtonElement>('#auto-keyframe-button')
    if (btn === null) return
    // Disable when there's no writable clip — the toggle would silently
    // no-op on bake otherwise, leaving the user wondering why their edits
    // aren't sticking.
    const applicable = this.is_animation_user_authored(this.current_playing_index) &&
      this.skinned_meshes_to_animate.length > 0
    btn.disabled = !applicable
    // Show as "active" only if both enabled AND applicable — keeps the
    // visual honest.
    btn.classList.toggle('active-button', this.auto_keyframe_enabled && applicable)
    btn.setAttribute('aria-pressed', (this.auto_keyframe_enabled && applicable) ? 'true' : 'false')
  }

  // --- Clip blending -----------------------------------------------------

  /**
   * Open a dialog to blend two existing clips into a new user-authored clip.
   * Pick A and B from dropdowns, choose a weight (0..1), click Bake. The
   * result is appended to the loaded clips list and selected.
   */
  public show_clip_blend_dialog (): void {
    if (this.animation_clips_loaded.length < 2) {
      new ModalDialog('Cannot blend clips', 'Need at least two clips loaded.').show()
      return
    }
    if (this.skinned_meshes_to_animate.length === 0) {
      new ModalDialog('Cannot blend clips', 'No model loaded.').show()
      return
    }

    const options_html = this.animation_clips_loaded.map((pair, i) => {
      const name = pair.display_animation_clip.name
      return `<option value="${i}">${i}: ${name}</option>`
    }).join('')

    const html = `
      <p>Bake the weighted blend of two clips into a new clip. 0 = pure A, 1 = pure B.</p>
      <div style="display: flex; flex-direction: column; gap: 0.5rem;">
        <label style="display: flex; align-items: center; gap: 0.5rem;">
          <span style="min-width: 70px;">Clip A</span>
          <select id="blend-clip-a" style="flex: 1;">${options_html}</select>
        </label>
        <label style="display: flex; align-items: center; gap: 0.5rem;">
          <span style="min-width: 70px;">Clip B</span>
          <select id="blend-clip-b" style="flex: 1;">${options_html}</select>
        </label>
        <label style="display: flex; align-items: center; gap: 0.5rem;">
          <span style="min-width: 70px;">Weight</span>
          <input type="range" id="blend-weight" min="0" max="1" step="0.05" value="0.5" style="flex: 1;" />
          <span id="blend-weight-readout" style="min-width: 36px; text-align: right;">0.50</span>
        </label>
      </div>
      <p style="margin-top: 0.75rem;">
        <button type="button" id="blend-bake-button">Bake blended clip</button>
      </p>
    `
    const dialog = new ModalDialog('Blend two clips', html, { customClass: 'clip-blend-dialog' })
    dialog.show()

    // Default A to current playing clip, B to next.
    const select_a = document.querySelector<HTMLSelectElement>('#blend-clip-a')
    const select_b = document.querySelector<HTMLSelectElement>('#blend-clip-b')
    const weight_input = document.querySelector<HTMLInputElement>('#blend-weight')
    const weight_readout = document.querySelector<HTMLSpanElement>('#blend-weight-readout')
    const bake_button = document.querySelector<HTMLButtonElement>('#blend-bake-button')
    if (select_a !== null && select_b !== null) {
      select_a.value = String(Math.max(0, this.current_playing_index))
      select_b.value = String(Math.min(this.animation_clips_loaded.length - 1, Math.max(0, this.current_playing_index) + 1))
    }
    weight_input?.addEventListener('input', () => {
      if (weight_readout !== null) weight_readout.textContent = parseFloat(weight_input.value).toFixed(2)
    })
    bake_button?.addEventListener('click', () => {
      const a_index = parseInt(select_a?.value ?? '0', 10)
      const b_index = parseInt(select_b?.value ?? '0', 10)
      const weight = parseFloat(weight_input?.value ?? '0.5')
      if (a_index === b_index) {
        new ModalDialog('Pick different clips', 'Clip A and Clip B must be different.').show()
        return
      }
      this.bake_blended_clip(a_index, b_index, weight)
      dialog.remove()
    })
  }

  /**
   * Build a blended clip from two source clips and append it as a new user-
   * authored clip. Selects the new clip on completion.
   */
  public bake_blended_clip (a_index: number, b_index: number, weight: number): void {
    if (a_index < 0 || a_index >= this.animation_clips_loaded.length) return
    if (b_index < 0 || b_index >= this.animation_clips_loaded.length) return
    const clip_a = this.animation_clips_loaded[a_index].display_animation_clip
    const clip_b = this.animation_clips_loaded[b_index].display_animation_clip
    const blended_clip = ClipBlender.blend(clip_a, clip_b, weight)

    // Build a user-authored clip pair from the blended AnimationClip.
    const new_index = this.create_and_select_user_authored_clip()
    if (new_index === -1) return
    const new_pair = this.animation_clips_loaded[new_index]
    new_pair.display_animation_clip = blended_clip
    new_pair.original_animation_clip = blended_clip.clone()
    this.play_animation(new_index)
    this.schedule_save_user_clips()
    this.schedule_thumbnail_capture()
  }

  // --- Curves editor -----------------------------------------------------

  /**
   * Open a modal hosting the per-track curves editor for the active clip.
   * Edits write back to `display_animation_clip` in place; the player is
   * re-bound on close so playback reflects the changes.
   */
  public show_curves_editor (): void {
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined) {
      new ModalDialog('Cannot edit curves', 'No clip selected.').show()
      return
    }
    // Close any prior curves-editor dialog before opening a new one. Stops
    // the user from accumulating multiple editors all targeting different
    // clips.
    this.open_curves_editor_dialog?.remove()

    const dialog = new ModalDialog('Curves editor', '<div id="curves-editor-host"></div>',
      { customClass: 'curves-editor-dialog' })
    dialog.show()
    const host = document.querySelector<HTMLDivElement>('#curves-editor-host')
    if (host === null) return

    // Capture the pair the editor targets at mount time. If the user
    // switches the active clip while the dialog is open, we'll auto-close
    // the dialog (in `play_animation`) so edits never silently land on a
    // clip the user is no longer looking at.
    const target_pair = pair
    let dirty = false
    const editor = new CurvesEditor({
      container: host,
      clip: target_pair.display_animation_clip,
      on_change: () => { dirty = true }
    })
    editor.mount()
    this.open_curves_editor_dialog = dialog

    const original_remove = dialog.remove.bind(dialog)
    dialog.remove = (): void => {
      editor.unmount()
      this.open_curves_editor_dialog = null
      if (dirty) {
        // Mirror edits onto the original clip + rebind the mixer for the
        // SAME pair the editor targeted (not whatever `current_playing_index`
        // is now — that may have moved on).
        target_pair.original_animation_clip = target_pair.display_animation_clip.clone()
        const target_index = this.animation_clips_loaded.indexOf(target_pair)
        if (target_index === this.current_playing_index) {
          this.play_animation(this.current_playing_index)
        }
        this.schedule_save_user_clips()
      }
      original_remove()
    }
  }

  // --- Onion skinning ----------------------------------------------------

  public is_onion_skin_enabled (): boolean {
    return this.onion_skin_manager?.is_enabled() ?? false
  }

  /**
   * Force-disable onion skinning. Idempotent. Called on step navigation, on
   * model switch in Explore mode, and when entering a mode (mocap recording,
   * offline video processing) that would render the ghosts misleading.
   * Updates button visual to match.
   */
  public disable_onion_skin (): void {
    if (this.onion_skin_manager?.is_enabled() === true) {
      this.onion_skin_manager.disable()
    }
    this.update_onion_skin_button_state()
  }

  public toggle_onion_skin (): void {
    if (this.scene === null) return
    if (this.skinned_meshes_to_animate.length === 0) return
    const clip = this.animation_clips_loaded[this.current_playing_index]?.display_animation_clip
    if (clip === undefined) return
    if (this.onion_skin_manager === null) {
      this.onion_skin_manager = new OnionSkinManager(this.scene)
    }
    if (this.onion_skin_manager.is_enabled()) {
      this.onion_skin_manager.disable()
    } else {
      this.onion_skin_manager.enable(this.skinned_meshes_to_animate[0], clip)
    }
    this.update_onion_skin_button_state()
  }

  private update_onion_skin_button_state (): void {
    const btn = document.querySelector<HTMLButtonElement>('#onion-skin-button')
    if (btn === null) return
    const enabled = this.is_onion_skin_enabled()
    btn.classList.toggle('active-button', enabled)
    btn.setAttribute('aria-pressed', enabled ? 'true' : 'false')
  }

  /**
   * Called by the engine on transform-controls drag-end while we're in
   * AnimationsListing step. If pose mode is active, the active clip is user-
   * authored, and auto-keyframe mode is on, bake the current pose at the
   * playhead. Cheap no-op in all other states.
   */
  public handle_transform_controls_drag_end (): void {
    if (!this.auto_keyframe_enabled) return
    if (!this.is_pose_mode_active()) return
    if (!this.is_animation_user_authored(this.current_playing_index)) return
    this.bake_pose_into_active_clip()
  }

  /**
   * Push the current clip's keyframe times into the AnimationPlayer so it can
   * render markers on the scrubber. Only user-authored clips get markers — library
   * and imported clips have dense per-frame tracks where every "time" is a
   * keyframe, which would render as a wall of pins. Also re-evaluates the
   * prev/next/delete-keyframe button enable state.
   */
  private update_keyframe_markers (): void {
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair !== undefined && pair.metadata.source_type === 'user-authored') {
      const times = KeyframeBuilder.collect_keyframe_times(pair.display_animation_clip)
      this.animation_player.set_keyframe_markers(times)
    } else {
      this.animation_player.clear_keyframe_markers()
    }
    this.update_keyframe_nav_buttons_state()
  }

  /**
   * Enable/disable the prev-keyframe, next-keyframe, and delete-keyframe-at-playhead
   * buttons based on the active clip and current playhead position. Called whenever
   * the active clip changes or the playhead moves.
   */
  private update_keyframe_nav_buttons_state (): void {
    const is_user_clip = this.is_animation_user_authored(this.current_playing_index)
    const keyframe_count = this.animation_player.get_keyframe_times().length
    const has_keyframes = is_user_clip && keyframe_count > 0

    if (this.ui.dom_prev_keyframe_button !== null) {
      this.ui.dom_prev_keyframe_button.disabled = !has_keyframes
    }
    if (this.ui.dom_next_keyframe_button !== null) {
      this.ui.dom_next_keyframe_button.disabled = !has_keyframes
    }
    if (this.ui.dom_delete_keyframe_button !== null) {
      this.ui.dom_delete_keyframe_button.disabled = !(is_user_clip && this.animation_player.is_playhead_on_keyframe())
    }
  }

  /**
   * Removes any keyframe at the current playhead time from the active user-authored
   * clip. No-op if no clip is active, the clip isn't user-authored, or no keyframe
   * exists within `KeyframeBuilder.TIME_EPSILON` of the playhead. Returns true on
   * successful removal so callers / tests can assert.
   */
  public delete_keyframe_at_playhead (): boolean {
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return false

    const time = this.animation_player.current_time()
    const new_display_clip = KeyframeBuilder.remove_keyframe_at_time(pair.display_animation_clip, time)
    // remove_keyframe_at_time always returns a new clip; check whether anything
    // actually changed by comparing total keyframe counts across all tracks.
    const old_count = this.count_keyframes(pair.display_animation_clip)
    const new_count = this.count_keyframes(new_display_clip)
    if (old_count === new_count) return false

    // Push undo state AFTER the no-op guard so we don't pollute history with
    // empty entries when the playhead is between keyframes.
    this.push_undo_state_for_active_clip()

    pair.display_animation_clip = new_display_clip
    pair.original_animation_clip = KeyframeBuilder.remove_keyframe_at_time(pair.original_animation_clip, time)

    // Re-bind the mixer to the new clip and refresh markers.
    this.play_animation(this.current_playing_index)
    this.schedule_save_user_clips()
    this.schedule_thumbnail_capture()
    return true
  }

  private count_keyframes (clip: AnimationClip): number {
    let total = 0
    for (const track of clip.tracks) total += track.times.length
    return total
  }

  /**
   * Capture a thumbnail of the current viewport and store it in the active
   * user-authored clip's metadata. Called immediately after an authoring action
   * that mutated the clip — the viewport is already showing the just-baked pose.
   *
   * No-op for non-user-authored clips, when no renderer canvas is wired, or
   * when the canvas isn't yet rendered. Persistence kicks in via the next
   * scheduled save.
   */
  private capture_thumbnail_for_active_clip (): void {
    if (this.renderer_canvas === null) return
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return

    const data_url = ClipThumbnailCapture.capture(this.renderer_canvas)
    if (data_url === null) return
    pair.metadata.thumbnail_data_url = data_url
  }

  /**
   * Debounced thumbnail capture so a 30Hz mocap bake stream doesn't trigger 30
   * captures per second. Coalesces to one capture ~600ms after the last call.
   */
  private schedule_thumbnail_capture (): void {
    if (this.thumbnail_debounce_timer !== null) clearTimeout(this.thumbnail_debounce_timer)
    this.thumbnail_debounce_timer = setTimeout(() => {
      this.thumbnail_debounce_timer = null
      this.capture_thumbnail_for_active_clip()
      this.schedule_save_user_clips()
      // Re-render the clip card so the thumbnail shows up without waiting for
      // the next list refresh.
      this.refresh_active_clip_thumbnail_in_ui()
    }, 600)
  }

  /**
   * Replace the existing thumbnail `<img>` (if any) on the active clip's card
   * with the freshly-captured data URL. Cheaper than re-rendering the whole list.
   */
  private refresh_active_clip_thumbnail_in_ui (): void {
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined) return
    const data_url = pair.metadata.thumbnail_data_url
    if (data_url === undefined) return
    if (this.ui.dom_animation_clip_list === null) return
    const card = this.ui.dom_animation_clip_list.querySelector<HTMLElement>(`button.secondary-button.play[data-index="${this.current_playing_index}"]`)
    const placeholder = card?.querySelector<HTMLElement>('.anim-preview-placeholder')
    if (placeholder === null || placeholder === undefined) return
    let img = placeholder.querySelector<HTMLImageElement>('img.user-clip-thumbnail')
    if (img === null) {
      img = document.createElement('img')
      img.className = 'user-clip-thumbnail'
      img.alt = ''
      placeholder.appendChild(img)
    }
    img.src = data_url
  }

  /**
   * Get the undo/redo stack for the given pair. Creates one lazily on first
   * access — every user-authored clip gets its own isolated history.
   */
  private undo_redo_stack_for (pair: TransformedAnimationClipPair): ClipUndoRedoStack {
    let stack = this.undo_redo_stacks.get(pair)
    if (stack === undefined) {
      stack = new ClipUndoRedoStack()
      this.undo_redo_stacks.set(pair, stack)
    }
    return stack
  }

  /**
   * Snapshot the active user-authored clip's *current* state onto its undo stack
   * BEFORE the caller mutates it. Call this immediately before any KeyframeBuilder
   * operation that produces a new clip.
   */
  private push_undo_state_for_active_clip (): void {
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return
    const stack = this.undo_redo_stack_for(pair)
    stack.push_state(pair.display_animation_clip, pair.original_animation_clip)
    this.update_undo_redo_buttons_state()
  }

  /**
   * Reverts the active user-authored clip to its previous state. Returns true on
   * success, false if there's nothing to undo.
   */
  public undo_active_clip (): boolean {
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return false
    const stack = this.undo_redo_stack_for(pair)
    const previous = stack.undo(pair.display_animation_clip, pair.original_animation_clip)
    if (previous === null) return false

    pair.display_animation_clip = previous.display_clip
    pair.original_animation_clip = previous.original_clip
    this.play_animation(this.current_playing_index)
    this.schedule_save_user_clips()
    this.update_undo_redo_buttons_state()
    return true
  }

  public redo_active_clip (): boolean {
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return false
    const stack = this.undo_redo_stack_for(pair)
    const next = stack.redo(pair.display_animation_clip, pair.original_animation_clip)
    if (next === null) return false

    pair.display_animation_clip = next.display_clip
    pair.original_animation_clip = next.original_clip
    this.play_animation(this.current_playing_index)
    this.schedule_save_user_clips()
    this.update_undo_redo_buttons_state()
    return true
  }

  private update_undo_redo_buttons_state (): void {
    const pair = this.animation_clips_loaded[this.current_playing_index]
    const is_user_clip = pair !== undefined && pair.metadata.source_type === 'user-authored'
    const stack = is_user_clip ? this.undo_redo_stacks.get(pair) : undefined

    if (this.ui.dom_undo_clip_button !== null) {
      this.ui.dom_undo_clip_button.disabled = !(is_user_clip && stack !== undefined && stack.can_undo())
    }
    if (this.ui.dom_redo_clip_button !== null) {
      this.ui.dom_redo_clip_button.disabled = !(is_user_clip && stack !== undefined && stack.can_redo())
    }
    this.update_pose_ops_buttons_state()
  }

  /**
   * Enable state for the copy/paste/mirror pose buttons. Copy + mirror need a
   * skinned mesh + a user-authored clip selected. Paste also needs a non-empty
   * clipboard.
   */
  private update_pose_ops_buttons_state (): void {
    const has_mesh = this.skinned_meshes_to_animate.length > 0
    const is_user_clip = this.is_animation_user_authored(this.current_playing_index)
    const can_capture = has_mesh && is_user_clip

    if (this.ui.dom_copy_pose_button !== null) {
      this.ui.dom_copy_pose_button.disabled = !can_capture
    }
    if (this.ui.dom_mirror_pose_button !== null) {
      this.ui.dom_mirror_pose_button.disabled = !can_capture
    }
    if (this.ui.dom_paste_pose_button !== null) {
      this.ui.dom_paste_pose_button.disabled = !(can_capture && this.pose_clipboard !== null)
    }
  }

  /**
   * Captures the current skeleton pose at the playhead and stores it in the
   * pose clipboard. The skeleton is whatever the mixer is showing right now —
   * which is what the user sees on screen. Returns true if anything was captured.
   */
  public copy_pose_at_playhead (): boolean {
    if (this.skinned_meshes_to_animate.length === 0) return false
    if (!this.is_animation_user_authored(this.current_playing_index)) return false

    const rig = RigConfig.by_skeleton_type(this.skeleton_type)
    const position_bone_names = rig?.position_tracking_bone_name !== undefined
      ? [rig.position_tracking_bone_name]
      : []

    const time = this.animation_player.current_time()
    this.pose_clipboard = PoseSnapshotUtil.capture(
      this.skinned_meshes_to_animate[0].skeleton,
      time,
      { position_bone_names }
    )
    this.update_pose_ops_buttons_state()
    return true
  }

  /**
   * Pastes the clipboard pose as a new keyframe at the current playhead. Pushes
   * an undo state first. Returns true on success.
   */
  public paste_pose_at_playhead (): boolean {
    if (this.pose_clipboard === null) return false
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return false

    const time = this.animation_player.current_time()
    const retimed = PoseOps.at_time(this.pose_clipboard, time)

    this.push_undo_state_for_active_clip()
    pair.display_animation_clip = KeyframeBuilder.upsert_pose_keyframe(pair.display_animation_clip, retimed)
    pair.original_animation_clip = KeyframeBuilder.upsert_pose_keyframe(pair.original_animation_clip, retimed)

    this.play_animation(this.current_playing_index)
    this.schedule_save_user_clips()
    this.schedule_thumbnail_capture()
    return true
  }

  /**
   * Captures the live pose, mirrors it left↔right, and bakes it at the current
   * playhead. Useful for symmetric animations (e.g. step-with-left-foot at t=1
   * → mirror → step-with-right-foot at t=2).
   */
  public mirror_pose_at_playhead (): boolean {
    if (this.skinned_meshes_to_animate.length === 0) return false
    const pair = this.animation_clips_loaded[this.current_playing_index]
    if (pair === undefined || pair.metadata.source_type !== 'user-authored') return false

    const rig = RigConfig.by_skeleton_type(this.skeleton_type)
    const position_bone_names = rig?.position_tracking_bone_name !== undefined
      ? [rig.position_tracking_bone_name]
      : []

    const time = this.animation_player.current_time()
    const live_pose = PoseSnapshotUtil.capture(
      this.skinned_meshes_to_animate[0].skeleton,
      time,
      { position_bone_names }
    )
    const mirrored = PoseOps.mirror(live_pose)

    this.push_undo_state_for_active_clip()
    pair.display_animation_clip = KeyframeBuilder.upsert_pose_keyframe(pair.display_animation_clip, mirrored)
    pair.original_animation_clip = KeyframeBuilder.upsert_pose_keyframe(pair.original_animation_clip, mirrored)

    this.play_animation(this.current_playing_index)
    this.schedule_save_user_clips()
    return true
  }

  /**
   * Gates the three pose-and-bake authoring buttons on whether a skinned mesh is
   * present. Without this, "+ New Clip" / "Pose Mode" silently no-op when the user
   * is on the AnimationsListing step before model load completed (e.g. a slow GLB).
   * Called from `begin()`, after mesh swap, and after the default-anim load completes.
   */
  private update_authoring_buttons_enabled_state (): void {
    const has_mesh = this.skinned_meshes_to_animate.length > 0
    if (this.ui.dom_new_user_clip_button !== null) {
      this.ui.dom_new_user_clip_button.disabled = !has_mesh
    }
    if (this.ui.dom_pose_mode_button !== null) {
      this.ui.dom_pose_mode_button.disabled = !has_mesh
    }
    this.update_bake_button_enabled()
    this.update_mocap_button_state()
    this.update_record_button_state()
  }

  /**
   * Returns the skinned meshes currently used for animation playback.
   * This is used during the download/export process to retrieved final skinned mesh
   */
  public active_skinned_meshes (): SkinnedMesh[] {
    return this.skinned_meshes_to_animate
  }

  public set_model_variation_switcher (switcher: ModelVariationSwitcher): void {
    this.model_variation_switcher = switcher
  }

  /**
   * Removes the current skinned meshes from the scene, adds the new ones,
   * and replays the current animation on them.
   */
  public swap_skinned_meshes (scene: Scene, new_skinned_meshes: SkinnedMesh[]): void {
    const is_variation_active = this.model_variation_switcher?.is_variation_active ?? false
    this.clear_variation_model_from_scene()

    if (!is_variation_active) {
      // remove the original skinned meshes that were added individually
      for (const mesh of this.skinned_meshes_to_animate) {
        Utility.remove_object_with_children(mesh)
      }
    }

    // add individual skinned meshes to scene (bones are children of the mesh after normalization)
    if (this.model_variation_switcher !== null) {
      this.model_variation_switcher.is_variation_active = true
    }
    for (const mesh of new_skinned_meshes) {
      scene.add(mesh)
    }

    this.skinned_meshes_to_animate = new_skinned_meshes
    this.update_authoring_buttons_enabled_state()

    // replay current animation on the new meshes
    this.play_animation(this.current_playing_index)
    this.animation_player.play()
  }

  /**
   * Clears any variation model root that was injected during model variation swapping.
   * This should be called before doing a full model load flow.
   */
  public clear_variation_model_from_scene (): void {
    const is_variation_active = this.model_variation_switcher?.is_variation_active ?? false
    if (is_variation_active) {
      for (const mesh of this.skinned_meshes_to_animate) {
        Utility.remove_object_with_children(mesh)
      }
      if (this.model_variation_switcher !== null) {
        this.model_variation_switcher.is_variation_active = false
      }
    }
  }

  public load_and_apply_default_animation_to_skinned_mesh (final_skinned_meshes: SkinnedMesh[]): void {
    this.skinned_meshes_to_animate = final_skinned_meshes
    this.update_authoring_buttons_enabled_state()

    // Set the animations file path on the loader
    this.animation_loader.set_animations_file_path(this.animations_file_path)

    this.is_loading_default_animations = true
    this.custom_animation_importer.set_enabled(false)

    // Reset the animation clips loaded
    this.animation_clips_loaded = []
    // Create an animation mixer to do the playback. Play the first by default
    this.animation_mixer = new AnimationMixer(new Object3D())

    // Load animations using the new AnimationLoader
    this.animation_loader.load_animations(this.skeleton_type, this.skeleton_scale)
      .then((loaded_clips: TransformedAnimationClipPair[]) => {
        this.animation_clips_loaded = loaded_clips
        this.onAllAnimationsLoaded()
      })
      .catch((error: Error) => {
        this.is_loading_default_animations = false
        this.custom_animation_importer.set_enabled(true)
        new ModalDialog('Animation library failed', animation_load_failed_message(error)).show()
      })
  }

  private onAllAnimationsLoaded (): void {
    this.is_loading_default_animations = false
    this.custom_animation_importer.set_enabled(true)

    // Restore previously-saved user-authored clips for this skeleton type. Async
    // so the default-library list renders immediately; the saved clips append
    // when IDB returns. We only do this once per skeleton load — guarded by
    // checking whether any user-authored clips are already in the list.
    const has_user_clips_already = this.animation_clips_loaded.some(p => p.metadata.source_type === 'user-authored')
    if (!has_user_clips_already) {
      void this.restore_user_clips_async()
    }

    this.sort_clips_grouped(this.animation_clips_loaded)

    // create user interface with all available animation clips
    this.build_animation_clip_ui(
      this.animation_clips_loaded,
      this.theme_manager
    )

    this.bind_animation_search_events()
  }

  private bind_animation_search_events (): void {
    this.animation_search?.addEventListener('export-options-changed', () => {
      if (this.ui.dom_animation_count != null) {
        this.ui.dom_animation_count.innerHTML = this.animation_search?.get_selected_animation_indices().length.toString() ?? '0'
      }
    })

    this.update_filtered_animation_listing_ui()
    this.animation_search?.addEventListener('filtered-animations-listing', () => {
      this.update_filtered_animation_listing_ui()
    })
  }

  private onAnimationLoadProgress (progress: AnimationLoadProgress): void {
    if (this.ui.dom_loading_progress_bar !== null) {
      this.ui.dom_loading_progress_bar.style.width = `${progress.percentage}%`

      const mb_loaded: string = (progress.overallBytesLoaded / (1024 * 1024)).toFixed(1)
      const mb_total: string = (progress.overallBytesTotal / (1024 * 1024)).toFixed(1)
      this.ui.dom_loading_progress_bar.textContent = `${mb_loaded} / ${mb_total} MB`
    }

    if (this.ui.dom_current_file_progress_bar !== null) {
      this.ui.dom_current_file_progress_bar.style.width = `${progress.currentFileProgress}%`
    }

    // if we are done loading, we can hide the container
    if (progress.percentage >= 100) {
      if (this.ui.dom_animation_progress_loader_container !== null) {
        this.ui.dom_animation_progress_loader_container.style.display = 'none'
      }
    } else {
      // make sure it is visible while loading
      if (this.ui.dom_animation_progress_loader_container !== null) {
        this.ui.dom_animation_progress_loader_container.style.display = 'flex'
      }
    }

    // can potentially shows file name loading...not sure if we need to actually show this.
    // if (this.ui.dom_loading_status_text !== null && progress.currentFile !== '') {
    //   const file_name = progress.currentFile.split('/').pop() ?? progress.currentFile
    //   this.ui.dom_loading_status_text.textContent = `Loading ${file_name}...`
    // }
  }

  private update_filtered_animation_listing_ui (): void {
    const animation_length_string: string = this.animation_search?.filtered_animations().length.toString() ?? '0'
    if (this.ui.dom_animations_listing_count != null) {
      this.ui.dom_animations_listing_count.innerHTML = animation_length_string + ' animations'
    }
  }

  /**
   * Rebuilds all of the warped animations by applying the specified warps.
   */
  private rebuild_warped_animations (): void {
    // Reset all of the warped clips to the corresponding original clip.
    this.animation_clips_loaded.forEach((warped_clip: TransformedAnimationClipPair) => {
      warped_clip.display_animation_clip = AnimationUtility.deep_clone_animation_clip(warped_clip.original_animation_clip)
    })

    if (this.mirror_animations_enabled) {
      AnimationUtility.apply_animation_mirroring(this.animation_clips_loaded)
    }

    /// Apply the arm extension warp:
    AnimationUtility.apply_arm_extension_warp(this.animation_clips_loaded, this.warp_arm_amount)
  }

  /**
   * Not all animation files have root bone keyframes, so we need to make
   * sure this is reset between animations to fully reset the animation state
   * @param skinned_mesh
   */
  private reset_root_motion_position (skinned_mesh: SkinnedMesh): void {
    if (skinned_mesh.skeleton.bones.length > 0) {
      const root_bone = skinned_mesh.skeleton.bones[0] // should always be root bone
      root_bone.position.set(0, 0, 0)
      root_bone.updateMatrixWorld(true)
    }
  }

  private play_animation (index: number = 0): void {
    // Auto-exit Pose Mode on clip switch. Without this, the user's gizmo edits stay
    // attached to bones whose underlying clip just changed, and the next "Bake Pose"
    // would land in the wrong clip.
    this.exit_pose_mode()

    // Auto-close any open curves-editor dialog. The editor was bound to the
    // previously-active clip; leaving it open lets the user mutate a clip
    // they're no longer looking at.
    if (index !== this.current_playing_index && this.open_curves_editor_dialog !== null) {
      this.open_curves_editor_dialog.remove()
    }

    // Same problem for recording — if we're capturing into clip A and the user
    // selects clip B, the next mocap frame would bake into B with offset times.
    // Stop recording cleanly first.
    if (this.is_recording()) this.stop_recording()

    // No clip selected (e.g. user just entered Explore mode and hasn't picked one yet,
    // or a replay-trigger like the mirror toggle ran before any selection). Leave the
    // model in its bind pose and clear the player.
    if (index < 0 || index >= this.animation_clips_loaded.length) {
      this.current_playing_index = -1
      this.animation_mixer = new AnimationMixer(new Object3D())
      this.current_animation_actions = []
      this.animation_player.clear_animation()
      this.update_bake_button_enabled()
      this.update_record_button_state()
      this.update_keyframe_nav_buttons_state()
      this.update_undo_redo_buttons_state()
      return
    }

    this.current_playing_index = index

    // animation mixer has internal cache with animations. doing this helps clear it
    // otherwise modifications like arm extension will not update
    this.animation_mixer = new AnimationMixer(new Object3D())

    const all_animation_actions: AnimationAction[] = []

    this.skinned_meshes_to_animate.forEach((skinned_mesh: SkinnedMesh) => {
      this.reset_root_motion_position(skinned_mesh)

      const clip_to_play: AnimationClip = this.animation_clips_loaded[this.current_playing_index].display_animation_clip
      const anim_action: AnimationAction = this.animation_mixer.clipAction(clip_to_play, skinned_mesh)

      anim_action.stop()
      anim_action.play()

      // Collect all animation actions for the animation player
      all_animation_actions.push(anim_action)
    })

    // Update the animation player with the current animation and all actions
    if (all_animation_actions.length > 0) {
      const clip_to_play: AnimationClip = this.animation_clips_loaded[this.current_playing_index].display_animation_clip
      this.animation_player.set_animation(clip_to_play, all_animation_actions)
    }

    this.current_animation_actions = all_animation_actions
    this.update_bake_button_enabled()
    this.update_auto_keyframe_button_state()
    this.update_record_button_state()
    this.update_keyframe_markers()
    this.update_undo_redo_buttons_state()

    // If onion-skin ghosts are visible, point them at the new clip so they
    // track the just-selected animation rather than the old one.
    if (this.onion_skin_manager !== null && this.onion_skin_manager.is_enabled()) {
      const clip = this.animation_clips_loaded[this.current_playing_index]?.display_animation_clip
      if (clip !== undefined) this.onion_skin_manager.set_clip(clip)
    }
  }

  private update_download_button_enabled (): void {
    // see if any of the "export" checkboxes are active. if not we need to disable the "Download" button
    const animation_checkboxes = this.get_animated_selected_elements()
    const is_any_checkbox_checked: boolean = Array.from(animation_checkboxes).some((checkbox) => {
      return (checkbox as HTMLInputElement).checked
    })
    if (this.ui.dom_export_button != null) {
      this.ui.dom_export_button.disabled = !is_any_checkbox_checked
    }
  }

  private add_event_listeners (): void {
    // make sure to only add the event listeners once
    // this could be potentially called multiple times when going back and forth
    // between editing skeleton and this step
    if (this.has_added_event_listeners) {
      console.info('Event listeners already added to animation step. Skipping.')
      return
    }

    // Add progress event for when animation GLB file is downloading for skeleton
    this.animation_loader.addEventListener('progress', (event: Event) => {
      const progress = (event as CustomEvent<AnimationLoadProgress>).detail
      this.onAnimationLoadProgress(progress)
    })

    // event listener for animation clip list with changing the current animation
    if (this.ui.dom_animation_clip_list != null) {
      this.ui.dom_animation_clip_list.addEventListener('click', (event) => {
        this.update_download_button_enabled()

        if (event.target == null) return
        const target = event.target as HTMLElement
        const action_button = target.closest<HTMLButtonElement>('[data-clip-action]')
        if (action_button !== null) {
          event.stopPropagation()
          const action = action_button.getAttribute('data-clip-action')
          const animation_index = Number(action_button.getAttribute('data-index'))
          if (!Number.isFinite(animation_index)) return

          if (action === 'rename') void this.rename_user_authored_clip(animation_index)
          if (action === 'duplicate') this.duplicate_user_authored_clip(animation_index)
          if (action === 'export') void this.export_single_clip_as_glb(animation_index)
          if (action === 'trim') void this.trim_user_authored_clip(animation_index)
          if (action === 'delete') void this.delete_user_authored_clip(animation_index)
          return
        }

        const play_button = target.closest<HTMLButtonElement>('button.secondary-button.play')
        if (play_button !== null) {
          const animation_index_str = play_button.getAttribute('data-index')
          if (animation_index_str != null) {
            const animation_index: number = Number(animation_index_str)
            this.play_animation(animation_index)
            this.update_bake_button_enabled()
            this.update_record_button_state()
          }
        }
      })
    }

    // Phase 3.B — pose-and-bake authoring buttons
    this.ui.dom_new_user_clip_button?.addEventListener('click', () => {
      this.create_and_select_user_authored_clip()
    })

    this.ui.dom_bake_pose_button?.addEventListener('click', () => {
      this.bake_pose_into_active_clip()
    })

    this.ui.dom_auto_keyframe_button?.addEventListener('click', () => {
      this.toggle_auto_keyframe()
    })
    this.load_auto_keyframe_setting()

    this.ui.dom_onion_skin_button?.addEventListener('click', () => {
      this.toggle_onion_skin()
    })

    this.ui.dom_blend_clips_button?.addEventListener('click', () => {
      this.show_clip_blend_dialog()
    })

    this.ui.dom_curves_editor_button?.addEventListener('click', () => {
      this.show_curves_editor()
    })

    this.ui.dom_delete_keyframe_button?.addEventListener('click', () => {
      this.delete_keyframe_at_playhead()
    })

    this.ui.dom_undo_clip_button?.addEventListener('click', () => {
      this.undo_active_clip()
    })

    this.ui.dom_redo_clip_button?.addEventListener('click', () => {
      this.redo_active_clip()
    })

    this.ui.dom_copy_pose_button?.addEventListener('click', () => {
      this.copy_pose_at_playhead()
    })

    this.ui.dom_paste_pose_button?.addEventListener('click', () => {
      this.paste_pose_at_playhead()
    })

    this.ui.dom_mirror_pose_button?.addEventListener('click', () => {
      this.mirror_pose_at_playhead()
    })

    // Keyboard shortcuts: Ctrl/Cmd+Z for undo, Ctrl+Y or Ctrl+Shift+Z for redo.
    // Skip when the user is typing into an input/textarea/contenteditable so we
    // don't hijack their text editing.
    window.addEventListener('keydown', (event) => {
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable === true) return

      if (!(event.ctrlKey || event.metaKey)) return
      const key = event.key.toLowerCase()
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault()
        this.undo_active_clip()
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault()
        this.redo_active_clip()
      }
    })

    this.ui.dom_prev_keyframe_button?.addEventListener('click', () => {
      this.animation_player.jump_to_prev_keyframe()
    })

    this.ui.dom_next_keyframe_button?.addEventListener('click', () => {
      this.animation_player.jump_to_next_keyframe()
    })

    // Re-evaluate the delete-keyframe button enable state every time the playhead
    // moves (drag, marker-click, prev/next jump — all fire the scrubber's input event).
    this.ui.dom_animation_scrubber?.addEventListener('input', () => {
      this.update_keyframe_nav_buttons_state()
    })

    this.ui.dom_pose_mode_button?.addEventListener('click', () => {
      this.toggle_pose_mode()
    })

    this.ui.dom_mocap_button?.addEventListener('click', () => {
      void this.toggle_mocap()
    })

    this.ui.dom_record_mocap_button?.addEventListener('click', () => {
      this.toggle_record_mocap('replace')
    })

    this.ui.dom_append_record_mocap_button?.addEventListener('click', () => {
      this.toggle_record_mocap('append')
    })

    this.ui.dom_mocap_settings_button?.addEventListener('click', () => {
      this.show_mocap_settings()
    })

    this.ui.dom_export_project_button?.addEventListener('click', () => {
      void ProjectArchive.download(this.skeleton_type, this.animation_clips_loaded)
    })

    this.ui.dom_import_project_button?.addEventListener('click', () => {
      this.ui.dom_import_project_input?.click()
    })

    this.ui.dom_import_spec_button?.addEventListener('click', () => {
      this.ui.dom_import_spec_input?.click()
    })

    this.ui.dom_import_spec_input?.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (file === undefined) return
      file.text().then((text) => {
        this.import_animation_spec(text)
      }).catch((err) => {
        new ModalDialog('Could not read spec', escape_html(String(err))).show()
      })
      // Reset so re-importing the same file fires `change` again.
      target.value = ''
    })

    this.ui.dom_import_project_input?.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (file === undefined) return
      void import_project_file(file, this)
      // Reset so re-importing the same file fires `change` again.
      target.value = ''
    })

    this.ui.dom_clear_user_clips_button?.addEventListener('click', () => {
      void confirm_action({
        title: 'Delete all saved clips',
        message: 'Permanently delete all of your saved authored clips (mocap recordings and pose-and-bake clips) across every skeleton type? This cannot be undone.',
        confirm_label: 'Delete all',
        danger: true
      }).then((confirmed) => {
        if (confirmed) void this.clear_all_saved_user_clips()
      })
    })

    // Make the webcam preview overlay draggable. The panel attaches its own
    // listeners — we just need to instantiate it once. Position persists in
    // localStorage and is clamped into the viewport on restore.
    if (this.ui.dom_mocap_preview_container !== null && this.mocap_preview_draggable === null) {
      this.mocap_preview_draggable = new DraggablePanel(
        this.ui.dom_mocap_preview_container,
        'meshinator-mocap-preview-position'
      )
    }

    // reset A-Pose arm extension button
    this.ui.dom_reset_arm_extension_button?.addEventListener('click', () => {
      const extend_arm_value: number = 0 // reset to zero
      if (this.ui.dom_extend_arm_numeric_input !== null) {
        this.ui.dom_extend_arm_numeric_input.value = extend_arm_value.toString()
      }
      if (this.ui.dom_extend_arm_range_input !== null) {
        this.ui.dom_extend_arm_range_input.value = extend_arm_value.toString()
      }
      this.update_a_pose_value(extend_arm_value)
    })

    // A-Pose arm extension event listener
    this.ui.dom_extend_arm_numeric_input?.addEventListener('input', () => {
      const extend_arm_value: number = Utility.parse_input_number(this.ui.dom_extend_arm_numeric_input?.value)
      if (this.ui.dom_extend_arm_range_input !== null) {
        this.ui.dom_extend_arm_range_input.value = extend_arm_value.toString()
      }
      this.update_a_pose_value(extend_arm_value)
    })

    this.ui.dom_extend_arm_range_input?.addEventListener('input', () => {
      const extend_arm_value: number = Utility.parse_input_number(this.ui.dom_extend_arm_range_input?.value)
      if (this.ui.dom_extend_arm_numeric_input !== null) {
        this.ui.dom_extend_arm_numeric_input.value = extend_arm_value.toString()
      }
      this.update_a_pose_value(extend_arm_value)
    })

    // check for changes to mirror animations checkbox
    this.ui.dom_mirror_animations_checkbox?.addEventListener('change', () => {
      const is_checked: boolean = this.ui.dom_mirror_animations_checkbox?.checked ?? false
      this.mirror_animations_enabled = is_checked
      // Rebuild animations with or without mirroring
      this.rebuild_warped_animations()
      this.play_animation(this.current_playing_index)
    })

    // helps ensure we don't add event listeners multiple times
    this.has_added_event_listeners = true
  }

  // three different things might update this value: numeric input, range input, or reset button
  private update_a_pose_value (new_value: number): void {
    this.warp_arm_amount = new_value
    this.rebuild_warped_animations()
    this.play_animation(this.current_playing_index)
  }

  public build_animation_clip_ui (animation_clips_to_load: TransformedAnimationClipPair[], theme_manager: ThemeManager): void {
    // Initialize AnimationSearch if not already done
    // we could switch skeleton types using navigation, so need to re-create in case this happens
    this.animation_search = new AnimationSearch('animation-filter', 'animations-items', theme_manager, this.skeleton_type)

    // Use the animation search class to handle the UI
    this.animation_search.initialize_animations(animation_clips_to_load)
  }

  public get_animated_selected_elements (): NodeListOf<Element> {
    // this needs to be called ad-hoc as selections might change
    return document.querySelectorAll('#animations-items input[type="checkbox"]')
  }

  public get_animation_indices_to_export (): number[] {
    if (this.animation_search === null) {
      return []
    }
    return this.animation_search.get_selected_animation_indices()
  }
}

/**
 * Replace characters that are invalid in cross-platform filenames with
 * underscores, collapse runs, and trim leading/trailing dots/spaces.
 */
function sanitize_filename (raw: string): string {
  return raw
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[._\s]+|[._\s]+$/g, '')
    .slice(0, 96)
}


export function user_clip_unavailable_message (): string {
  return 'Finish loading and skinning a model before creating a user-authored clip.'
}

export function animation_load_failed_message (error: Error): string {
  const detail = error.message.trim()
  if (detail.length === 0) return 'The default animation library could not be loaded.'
  return `The default animation library could not be loaded: ${escape_html(detail)}`
}
